import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  YdsBadgeComponent,
  YdsButtonBackComponent,
  YdsCardComponent,
  YdsEmptyStatesComponent,
  YdsPaginationComponent,
  YdsSpinnerComponent,
  YdsTableCellDirective,
  YdsTableComponent,
  type YdsBadgeVariant,
  type YdsTableColumn,
} from '../../../../yds-angular';
import { ExportDownload, PayrolljournalService } from '../payrolljournal.service';

const SELECT_PAYROLL_JOURNAL_ROUTE = '/proces/export/exportgeneralledger/selectpayrolljournal';
const JOURNAL_PROCESS_LOG_ROUTE = '/proces/export/exportgeneralledger/journalprocesslog';

@Component({
  selector: 'app-downloadgeneralledger',
  imports: [
    YdsBadgeComponent,
    YdsButtonBackComponent,
    YdsCardComponent,
    YdsEmptyStatesComponent,
    YdsPaginationComponent,
    YdsSpinnerComponent,
    YdsTableCellDirective,
    YdsTableComponent,
  ],
  templateUrl: './downloadgeneralledger.component.html',
  styleUrl: './downloadgeneralledger.component.scss',
})
export class DownloadgeneralledgerComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'institutionName', header: 'Instelling' },
    { key: 'period', header: 'Periode', width: '90px' },
    { key: 'bookingGroupName', header: 'Boekingsgroep', width: '120px' },
    { key: 'journalPlanningRecordName', header: 'Verwerkingsdefinitie', width: '180px' },
    { key: 'exportDefinitionName', header: 'Exportdefinitie', width: '120px' },
    { key: 'changeDateTime', header: 'Run tijd', width: '110px' },
    { key: 'status', header: 'Status', width: '200px' },
    { key: 'download', header: 'Downloadlink', width: '120px' },
  ];

  private payrolljournalService = inject(PayrolljournalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private readonly opdrachtgeverId = this.route.snapshot.queryParamMap.get('opdrachtgeverId');
  private readonly institutionId = this.route.snapshot.queryParamMap.get('institutionId');
  private readonly bookingYear = Number(this.route.snapshot.queryParamMap.get('bookingYear'));
  private readonly bookingPeriod = Number(this.route.snapshot.queryParamMap.get('bookingPeriod'));
  private readonly initialPageIndex = Math.max(
    0,
    Number(this.route.snapshot.queryParamMap.get('pageIndex')) || 0,
  );

  exports = signal<ExportDownload[]>([]);
  isLoading = signal(true);
  loadError = signal<string | null>(null);
  downloadError = signal<string | null>(null);
  downloadingId = signal<string | null>(null);
  pageIndex = signal(0);

  pagedExports = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.exports().slice(start, start + this.pageSize);
  });

  tableItems = computed(() => {
    return this.pagedExports().map((item) => ({
      id: item.exportPlanningRecordId,
      journalPlanningRecordId: item.journalPlanningRecordId,
      fileName: item.fileName,
      exportStatus: item.exportStatus,
      errorMessage: item.errorMessage,
      institutionName: item.institutionName,
      period: `${item.bookingYear}/${String(item.bookingPeriod).padStart(2, '0')}`,
      bookingGroupName: item.bookingGroupName,
      journalPlanningRecordName: item.journalPlanningRecordName,
      exportDefinitionName: item.exportDefinitionName,
      changeDateTime: this.formatDateTime(item.changeDateTime),
      sendTo: item.sendTo,
    }));
  });

  private formatDateTime(value: string | null): string {
    if (!value) {
      return '';
    }
    // Backend sends UTC without a timezone marker, so add one to avoid local-time parsing.
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const date = new Date(hasTimezone ? value : `${value}Z`);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  ngOnInit(): void {
    if (
      !this.institutionId ||
      !Number.isFinite(this.bookingYear) ||
      !Number.isFinite(this.bookingPeriod)
    ) {
      this.isLoading.set(false);
      this.router.navigate([SELECT_PAYROLL_JOURNAL_ROUTE]);
      return;
    }

    this.payrolljournalService
      .getExportDownloadList(this.institutionId, this.bookingYear, this.bookingPeriod)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.exports.set(result);
          const lastPageIndex = Math.max(0, Math.ceil(result.length / this.pageSize) - 1);
          this.pageIndex.set(Math.min(this.initialPageIndex, lastPageIndex));
          this.isLoading.set(false);
        },
        error: () => {
          this.loadError.set('Downloadlijst laden mislukt.');
          this.isLoading.set(false);
        },
      });
  }

  isDone(exportStatus: string): boolean {
    return exportStatus === 'DONE';
  }

  isFileApiSend(sendTo: string): boolean {
    return sendTo === 'FILEAPI';
  }

  statusLabel(exportStatus: string): string {
    switch (exportStatus) {
      case 'DONE':
        return 'Gereed';
      case 'FAILED':
        return 'Mislukt';
      case 'TODO':
        return 'In wachtrij';
      default:
        return exportStatus;
    }
  }

  statusVariant(exportStatus: string): YdsBadgeVariant {
    switch (exportStatus) {
      case 'DONE':
        return 'default';
      case 'FAILED':
        return 'error';
      case 'TODO':
        return 'info';
      default:
        return 'muted';
    }
  }

  onDownload(event: Event, item: Record<string, unknown>): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.downloadingId()) {
      return;
    }

    this.downloadError.set(null);
    this.downloadingId.set(String(item['id']));

    this.payrolljournalService
      .downloadExportFile(
        String(item['journalPlanningRecordId']),
        String(item['fileName'] || 'Export'),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.downloadingId.set(null),
        error: () => {
          this.downloadError.set('Download export mislukt.');
          this.downloadingId.set(null);
        },
      });
  }

  onBack(): void {
    this.router.navigate([SELECT_PAYROLL_JOURNAL_ROUTE], {
      state: { opdrachtgeverId: this.opdrachtgeverId, instellingId: this.institutionId },
    });
  }

  onRowClick(item: Record<string, unknown>): void {
    const journalPlanningRecordId = String(item['journalPlanningRecordId'] ?? '');
    if (!journalPlanningRecordId) {
      return;
    }

    this.router.navigate([JOURNAL_PROCESS_LOG_ROUTE], {
      queryParams: {
        journalPlanningRecordId,
        opdrachtgeverId: this.opdrachtgeverId,
        institutionId: this.institutionId,
        bookingYear: this.bookingYear,
        bookingPeriod: this.bookingPeriod,
        pageIndex: this.pageIndex(),
      },
    });
  }
}
